import { useRef, useCallback } from 'react';

// Fallback wait duration in ms when the Web Animations API reports no running
// animations on the target. Approximately matches the entry animations used on
// reveal wrappers in the booking flow (380ms is a reasonable midpoint between
// fade-in and slide-in completions).
const REVEAL_FALLBACK_MS = 380;

/**
 * Returns a `scrollAfterReveal(ref, options?)` function that waits for any
 * in-progress CSS animations on the target element to finish before scrolling.
 *
 * Each call invalidates the previous pending scroll via a monotonic request
 * counter, so rapid stage transitions never pile up stale scroll requests.
 *
 * @returns {(ref: React.RefObject, options?: ScrollIntoViewOptions) => void}
 */
const useScrollAfterReveal = () => {
  // Each call increments this; closures compare their captured id to abort
  // if a newer scroll was requested before they executed.
  const requestIdRef = useRef(0);

  const scrollAfterReveal = useCallback((ref, options = {}) => {
    if (!ref?.current) return;

    const id = ++requestIdRef.current;

    const doScroll = () => {
      if (requestIdRef.current !== id) return;
      if (!ref.current) return;
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        ...options,
      });
    };

    // Web Animations API: if the element has running animations, wait for them
    // all to finish before scrolling so the layout has settled.
    const animations = ref.current.getAnimations?.() ?? [];
    const running = animations.filter(
      (a) => a.playState === 'running' || a.playState === 'pending'
    );

    if (running.length > 0) {
      Promise.all(running.map((a) => a.finished))
        .then(doScroll)
        // Animations cancelled mid-flight reject their finished promise. Use a
        // timeout rather than calling doScroll immediately so the layout has
        // time to settle after the interrupted transition.
        .catch(() => setTimeout(doScroll, REVEAL_FALLBACK_MS));
    } else {
      // No Web Animations detected; fall back to a timeout that covers the
      // CSS animation duration so the element has reached its final position.
      setTimeout(doScroll, REVEAL_FALLBACK_MS);
    }
  }, []);

  return scrollAfterReveal;
};

export default useScrollAfterReveal;
